import React, { useEffect } from "react";
import { useMediaQuery } from "@chakra-ui/react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  LinkBox,
  LinkOverlay,
  Text
} from "@chakra-ui/react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, A11y } from "swiper";
import enableSwiperClicksFromNav from "../../helpers/enableSwiperClicksFromNav";
import { handleAppendArrowsToSliders } from "../../helpers/handleAppendArrowsToSliders";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/a11y";
import "./sliderStories.scss";
import { SliderProps } from "../../types/homePageTypes";


// slider element with rectangular shape for year round home page

const SliderStories = ({ 
  title,
  description,
  isWebProgrammeSlider,
  sliderItems 
}: SliderProps) => {
  const [isLargerThan1536] = useMediaQuery("(min-width: 1536px)");
  const [isLargerThan1200] = useMediaQuery("(min-width: 1200px)");
  const [isLargerThan992] = useMediaQuery("(min-width: 992px)");
  const [isLargerThan748] = useMediaQuery("(min-width: 748px)");
  const [isLargerThan567] = useMediaQuery("(min-width: 567px)");
  const [isSmallerThan567] = useMediaQuery("(max-width: 566px)");
  const { swiperRef } = enableSwiperClicksFromNav();

  let slidesPerView;
  if (isSmallerThan567) {
    slidesPerView = 1.517;
  } else if (isLargerThan567 && !isLargerThan748) {
    slidesPerView = 1.61;
  } else if (isLargerThan748 && !isLargerThan1200) {
    slidesPerView = 1.75
  } else {
    slidesPerView = 2.289
  }

  // create an id for the swiper component
  let swiperId: string = title ? `stories-${title.replace(/[^a-zA-Z0-9]/g, '')}` : "stories-0";
  if (swiperId.length > 25) {
    swiperId = swiperId.substring(0, 25);
  }

  // add slider next and previous arrows to target areas
  const nextTarget = `#${ swiperId }.sliderStories .swiper-button-next`;
  const prevTarget = `#${ swiperId }.sliderStories .swiper-button-prev`;
  handleAppendArrowsToSliders(nextTarget, prevTarget);


  return (
    <Box
      marginBottom={{ base: "50px !important", sm: "80px !important" }} 
      marginTop="0 !important"
    >
      {/* section title */}
      <Heading
        as="h2"
        size={{ base: "section-header-mobile", sm: "section-header-tablet", lg: "section-header-desktop" }} 
        marginBottom={{ base: "12px !important", sm: "20px !important" }}
        marginLeft={{ base: "24px !important", sm: "28px !important" }}
      >
        { title }
      </Heading>

      {/* section description */}
      <Text
        size={{ base: "bravo-mobile", sm: "bravo", lg: "bravo-desktop" }}
        color="black !important"
        marginTop="0"
        marginBottom={{ base: "12px !important", sm: "20px !important" }}
        marginLeft={{ base: "24px !important", sm: "28px !important" }}
        p="0 30px 0 0"
      >
        { description }
      </Text>

      {/* mobile view */}
      {!isLargerThan1200 &&
        <>
          <Swiper
            id={ swiperId }
            className="sliderStories"
            modules={[ A11y ]}
            slidesPerView={ isLargerThan567 ? ( isLargerThan992 ? 2.66 : 2.36 ) : 2.409 }
            spaceBetween={ 10 }
          >
            {sliderItems.map(( slide, index ) => {
              return (
                <SwiperSlide key={ index }>
                  <LinkBox>
                    <LinkOverlay href={ slide.slug } aria-label={ slide.buttonText }>
                      <Image
                        h={`calc(${isLargerThan567 ? 36.75 : 39.75}vw * 1.49)`}
                        w="100%"
                        objectFit="cover"
                        src={ slide.posterUrl }
                        alt={ slide.posterAlt }
                      />
                    </LinkOverlay>
                  </LinkBox>
                  <Flex
                    w="100%"
                    p={{ base: "30px 20px 40px 20px", sm: "40px 30px 50px 30px" }}
                    direction="column"
                    justifyContent="flex-start !important"
                  >
                    {/* label */}
                    {slide.webProgramme.length > 0 &&
                      <Box marginBottom={{ base: "16px", sm: "21px" }}>
                        <Text
                          w="fit-content"
                          size={{ base: "program-tag-mobile", sm: "program-tag-tablet" }}
                          color="black !important"
                          borderColor="black !important"
                        >
                          {slide.webProgramme.map(( label, labelIndex ) => {
                              // allow only one label
                              if (labelIndex > 0) {
                                return null
                              }
                              return (
                                <Box as="span" key={ labelIndex }>
                                  { label.title }
                                </Box>
                            )})
                          }
                        </Text>
                      </Box>
                    }

                    {/* title */}
                    {slide.title &&
                      <Box marginBottom={{ base: "16px", sm: "21px" }}>
                        <Heading
                          as="h3"
                          size={{ base: "portrait-slider-header-mobile", sm: "portrait-slider-header-tablet" }}
                          textAlign="left"
                        >
                          { slide.title }
                        </Heading>
                      </Box>
                    }

                    {/* button */}
                    {slide.buttonText &&
                      <LinkBox>
                        <Button 
                          size={{ base: "poster-slider-mobile", sm: "poster-slider" }}
                          backgroundColor="#E31D40 !important"
                        >
                          <LinkOverlay href={ slide.slug }>
                            { slide.buttonText }
                          </LinkOverlay>
                        </Button>
                      </LinkBox>
                    }

                  </Flex>
                </SwiperSlide>
              )
            })}  
          </Swiper>

          {/* divider */}
          <Box
            backgroundColor="#000000"
            h={{ base: "5px", sm: "10px" }}
            w="100%"
            marginLeft="10px" 
          ></Box>
        </>
      }

      {/* desktop view */}
      {isLargerThan1200 &&
        <Swiper
          id={ swiperId }
          className="sliderStories"
          onSwiper={( swiperInstance ) => { swiperRef.current = swiperInstance }}
          modules={[ Navigation, A11y ]}
          navigation={ true }
          slidesPerView={ 3.518 }
          spaceBetween={ 10 }
        >
          {sliderItems.map(( slide, index ) => {
            return (
              <SwiperSlide key={ index }>
                <Image
                  h="calc(27.75vw * 1.49)"
                  src={ slide.posterUrl }
                  alt={ slide.posterAlt }
                  objectFit="cover"
                  _hover={{
                    filter: "blur(8px)",
                  }}
                  transition="0.3s ease-in-out"
                />
                <LinkBox
                  display="flex"
                  position="absolute"
                  top="0"
                  left="0"
                  h="100%"
                  w="100%"
                  p="67px 58px 32px 32px"
                  bg="rgba(0, 0, 0, 0.45)"
                  opacity="0"
                  _hover={{
                    opacity: "1",
                    transform: "translateX(0)"
                  }}
                  _focusWithin={{
                    opacity: "1",
                    transform: "translateX(0)"
                  }}
                  transition="0.3s ease-in-out, transform 0.3s ease-out"
                  flexDirection="column"
                  justifyContent="flex-end !important"
                  backdropFilter="blur(2px)"
                  transform="translateX(-5px)"
                >
                  <LinkOverlay href={ slide.slug } aria-label={slide.buttonText}>
                    {/* label */}
                    {slide.webProgramme.length > 0 &&
                      <Box marginBottom="23px">
                        <Text
                          w="fit-content"
                          size={{ base: "schedule-item-tag" }}
                        >
                          {slide.webProgramme.map(( label, labelIndex ) => {
                              // allow only one label
                              if (labelIndex > 0) {
                                return null
                              }

                              return (
                                <Box
                                  as="span"
                                  key={ labelIndex }
                                >
                                  { label.title }
                                </Box>
                            )})
                          }
                        </Text>
                      </Box>
                    }

                    {/* title */}
                    {slide.title &&
                      <Box marginBottom="23px">
                        <Heading
                          as="h3"
                          size={{ md: "portrait-slider-header-desktop" }}
                          textAlign="left"
                        >
                          { slide.title }
                        </Heading>
                      </Box>
                    }

                    {/* button */}
                    {slide.buttonText &&
                      <Button
                        size="poster-slider"
                        backgroundColor="#E31D40 !important"
                        _hover={{ bgColor: "#000000 !important", border: "none !important" }}
                      >
                        { slide.buttonText }
                      </Button>
                    }
                  </LinkOverlay>
                </LinkBox>
              </SwiperSlide>
            )
          })}  
        </Swiper>
      }
    </Box>
  );
}

export default SliderStories;